/**
 * The Odżywianie tab's reads and its one write.
 *
 * Three reads: the current target, the range the chart shows, and the 12-week range the „Tygodnie"
 * list and the week pane read. With the chart on 12 weeks the last two are ONE query. The tab waits
 * for all three (a section waits for all of its reads). A 404 on any of them is the module being off
 * for this client (R16) and is told as that, not as a failure.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { putNutritionCustomersCustomerIdTarget } from '~/shared/api/generated/endpoints/nutrition/nutrition';
import { ApiError, isGone } from '~/shared/api/mutator';

import {
  clientNutritionCalculatorQueryOptions,
  clientNutritionDayQueryOptions,
  clientNutritionRangeQueryOptions,
  clientNutritionRoot,
  clientNutritionTargetQueryKey,
  clientNutritionTargetQueryOptions,
} from '../api/clientDetailQueries';
import {
  toCalculatorProfile,
  toNutritionDayMeals,
  toNutritionRange,
  toNutritionTarget,
} from '../api/clientDetailSource';
import type { NutritionDayMeals, NutritionRange, NutritionSpan, NutritionTargetValues } from '../model/nutrition';
import type { CalculatorProfile } from '../model/nutritionCalculator';
import type { TargetSubmission } from '../model/nutritionTarget';

export interface NutritionReads {
  readonly target: NutritionTargetValues | null;
  /** The chart's range; `null` until it answers. While a new span loads, the previous span's. */
  readonly range: NutritionRange | null;
  /** The span `range` answers, which lags the chosen span until the new read lands. */
  readonly rangeSpan: NutritionSpan | null;
  /** The 12-week range; `null` until it answers. */
  readonly quarter: NutritionRange | null;
  readonly pending: boolean;
  /** The module is off for this client. */
  readonly unavailable: boolean;
  readonly failed: boolean;
  readonly retry: () => void;
}

export function useNutrition(clientId: number, span: NutritionSpan): NutritionReads {
  const target = useQuery(clientNutritionTargetQueryOptions(clientId));
  const range = useQuery(clientNutritionRangeQueryOptions(clientId, span));
  const quarter = useQuery(clientNutritionRangeQueryOptions(clientId, 'quarter'));
  const reads = [target, range, quarter];
  const unavailable = reads.some((read) => read.isError && isGone(read.error));
  return {
    target: target.data === undefined ? null : toNutritionTarget(target.data),
    range: range.data === undefined ? null : toNutritionRange(range.data.dto),
    rangeSpan: range.data?.span ?? null,
    quarter: quarter.data === undefined ? null : toNutritionRange(quarter.data.dto),
    pending: reads.some((read) => read.isPending),
    unavailable,
    failed: !unavailable && reads.some((read) => read.isError),
    retry: () => {
      for (const read of reads) if (read.isError) void read.refetch();
    },
  };
}

/**
 * Whether Odżywianie stands for this client. R16 opens the module for a pilot trainer AND for any
 * client who already has a target, and `/auth/me` only says the first. So a trainer off the pilot
 * asks the client's target once: a 200 is the module open for this client, a 404 is it off, and
 * `'pending'` is the question not yet answered.
 */
export function useNutritionTabStands(clientId: number, pilotTrainer: boolean): boolean | 'pending' {
  const probe = useQuery({ ...clientNutritionTargetQueryOptions(clientId), enabled: !pilotTrainer });
  if (pilotTrainer || probe.isSuccess) return true;
  return probe.isPending ? 'pending' : false;
}

export interface NutritionDayEntries {
  /** The client's meals in the server's order, each with its items, and the day total; `null` until read. */
  readonly day: NutritionDayMeals | null;
  readonly pending: boolean;
  readonly failed: boolean;
}

/** The meals of one opened day. */
export function useNutritionDayEntries(clientId: number, date: string): NutritionDayEntries {
  const day = useQuery(clientNutritionDayQueryOptions(clientId, date));
  return {
    day: day.data === undefined ? null : toNutritionDayMeals(day.data),
    pending: day.isPending,
    failed: day.isError,
  };
}

export interface NutritionCalculatorInputs {
  readonly profile: CalculatorProfile | null;
  readonly pending: boolean;
  readonly failed: boolean;
  /** Refused: the reader is not this client's trainer (`nutrition_calculator_trainer_only`). */
  readonly refused: boolean;
  readonly retry: () => void;
}

/** The calculator's inputs, read fresh each time the target sheet opens (R19). */
export function useNutritionCalculatorInputs(clientId: number): NutritionCalculatorInputs {
  const read = useQuery(clientNutritionCalculatorQueryOptions(clientId));
  const refused = read.error instanceof ApiError && read.error.status === 403;
  return {
    profile: read.data === undefined ? null : toCalculatorProfile(read.data),
    pending: read.isPending,
    failed: read.isError && !refused,
    refused,
    retry: () => {
      void read.refetch();
    },
  };
}

export interface NutritionTargetWrite {
  readonly save: (request: NonNullable<TargetSubmission['request']>, onSaved: () => void) => void;
  readonly saving: boolean;
  /** The backend's stable code of the last refusal, or `null` for a failure with none. */
  readonly errorCode: string | null;
  readonly failed: boolean;
  readonly reset: () => void;
}

export function useNutritionTargetWrite(clientId: number): NutritionTargetWrite {
  const queryClient = useQueryClient();
  const put = useMutation({
    mutationFn: (request: NonNullable<TargetSubmission['request']>) =>
      putNutritionCustomersCustomerIdTarget(clientId, request),
    onSuccess: (response) => {
      queryClient.setQueryData(clientNutritionTargetQueryKey(clientId), response);
      // A new target re-judges today, and today is in every range.
      void queryClient.invalidateQueries({ queryKey: clientNutritionRoot(clientId) });
    },
  });
  return {
    save: (request, onSaved) => {
      if (put.isPending) return;
      put.mutate(request, { onSuccess: onSaved });
    },
    saving: put.isPending,
    errorCode: put.error instanceof ApiError ? (put.error.code ?? null) : null,
    failed: put.isError,
    reset: put.reset,
  };
}
